/**
 *
 * v.0.1  10/09/25  Inicial
 * v.0.2  18/09/25  Añadido efecto Zebra en rows.
 *                  Añadido evento estilizadorRow
 *                  Añadido resize de columnas:
 *                  Añadido autoResizeCol.
 *                  Añadida la opción de ordenación, con ordenado personalizado por tipo de columna
 * v.0.3  09/10/25  Corrección: reintroducidas las funciones auxiliares y columnas fijas
 * v.0.5  03/06/26  Añadido soporte para CELLTIPO_HTML (WYSIWYG)
 */

import React from 'react'
import PropTypes from 'prop-types';
import {
    CELLTIPO_CADENA,
    CELLTIPO_ELIMINARROW,
    CELLTIPO_ESTADOROW,
    CELLTIPO_FECHA,
    CELLTIPO_ICONOACCION,
    CELLTIPO_MEMO,
    CELLTIPO_HTML,
} from "./TiposCeldas";
import {getValorCampo} from "../../../dental/utils/FormularioUtils";
import {Icon} from "@blueprintjs/core";
import DOMPurify from "dompurify";

const filasZebraColoresDefecto= ["#f9f9f9", "#ffffff"];
const FLASH_BATCH_WINDOW = 150; // ms para agrupar eventos
const FLASH_DURATION = 800;     // duración visual del flash

class Tabla extends React.Component {
    constructor(props) {
        super(props)

        const columnas = props.columnas ? this.prepararColumnas(props) : [];

        this.state = {
            columnas,
            datos: props.datos ? props.datos : [],
            colWidths: columnas.map(c => c.ancho || 150),
            resizing: null,
            hoverCol: null,
            sortConfig: null,
            elementoSeleccionadoCodigo: null,

            //Para mostrar los cambios
            celdasActualizadas: {},   // { "codigo|campo": true }
            filasModificadas: new Set(), // Set de codigos

            // Para mostrar la hora de modificacion
            // No dispara renders innecesarios porque solo cambia cuando hay eventos WS.
            celdasTimestamp: {}, // { "codigo|campo": Date }
        }

        // Batch de  Flashes para optimizar cuando viene una ráfaga de cambios
        this.flashBuffer = new Set();       // Set de "codigo|campo"
        this.flashFlushTimer = null;        // timeout del batch
        this.flashClearTimer = null;        // timeout de limpieza
    }

    componentDidUpdate(prevProps) {
        //Más restrictivo el cambio
        if (
            prevProps.datos !== this.props.datos &&
            this.props.datos !== this.state.datos
        ) {
            this.setState({ datos: this.props.datos });
        }
    }

    prepararColumnas = (props) => {
        let columnaE= { campo: "estadoRow", titulo:"E", name: "E", minWidth: 10, ancho: 30, tipo: CELLTIPO_ESTADOROW, resizable: false, fija: true,};
        let newColumnas = [columnaE, ...props.columnas];

        if (Boolean(props.mostrarEliminar)){
            let columnaEliminar= { campo: "eliminarRow", titulo: "X", name: "X", minWidth: 10, ancho: 30, tipo: CELLTIPO_ELIMINARROW, resizable: false};
            newColumnas.push(columnaEliminar);
        }
        return newColumnas;
    }

    // ************************************************
    // REDIMENSIONAMIENTO DE COLUMNAS
    // ************************************************

    handleMouseMoveTh = (e, index) => {
        if (this.state.resizing)
            return; // no cambiar el cursor mientras se redimensiona

        const th = e.currentTarget;
        const rect = th.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const edgeThreshold = 5; // píxeles desde el borde derecho

        if (rect.width - offsetX <= edgeThreshold) {
            th.style.cursor = "col-resize"; // cambiar cursor
            this.setState({ hoverCol: index });
        } else {
            th.style.cursor = "";
            this.setState({ hoverCol: null });
        }
    }

    handleMouseDownTh = (e, index) => {
        if (this.state.hoverCol === index) {
            this.setState({
                resizing: {
                    colIndex: index,
                    startX: e.clientX,
                    startWidth: this.state.colWidths[index]
                }
            });

            // Cambiar cursor a nivel global
            document.body.style.cursor = "col-resize";

            document.addEventListener("mousemove", this.onResize);
            document.addEventListener("mouseup", this.finalizarResize);
        }
    }

    onResize = (e) => {
        const { resizing, colWidths } = this.state;
        if (!resizing) return;

        const deltaX = e.clientX - resizing.startX;
        const newWidths = [...colWidths];
        newWidths[resizing.colIndex] = Math.max(50, resizing.startWidth + deltaX);
        this.setState({ colWidths: newWidths });
    }

    finalizarResize = () => {
        this.setState({ resizing: null });
        document.body.style.cursor = "";
        document.removeEventListener("mousemove", this.onResize);
        document.removeEventListener("mouseup", this.finalizarResize);
    }

    autoResizeCol = (e, index) => {
        const { columnas, datos, colWidths } = this.state;
        const columna = columnas[index];
        const th = e.currentTarget;
        const rect = th.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const edgeThreshold = 5;

        if (rect.width - offsetX <= edgeThreshold) {
            const table = th.closest("table");
            if (!table) return;

            const tds = Array.from(table.querySelectorAll(`tbody tr td:nth-child(${index + 1})`));
            let maxWidth = th.scrollWidth; // ancho del encabezado
            tds.forEach((td, rowIndex) => {
                let valor = getValorCampo(datos[rowIndex], columna.campo, "");
                const computedStyle = window.getComputedStyle(td);

                const div = document.createElement("div");
                div.style.visibility = "hidden";
                div.style.position = "absolute";
                div.style.whiteSpace = columna.tipo === CELLTIPO_MEMO || columna.tipo === CELLTIPO_HTML ? "pre-wrap" : "pre";
                div.style.fontSize = computedStyle.fontSize;
                div.style.fontFamily = computedStyle.fontFamily;
                div.style.fontWeight = computedStyle.fontWeight;
                div.style.padding = computedStyle.padding;
                div.style.letterSpacing = computedStyle.letterSpacing;
                div.style.boxSizing = computedStyle.boxSizing;
                div.innerText = valor;

                document.body.appendChild(div);
                const width = div.offsetWidth;
                if (width > maxWidth) maxWidth = width;
                document.body.removeChild(div);
            });

            const newWidths = [...colWidths];
            newWidths[index] = Math.max(maxWidth, this.state.columnas[index].minWidth || 30);
            this.setState({ colWidths: newWidths });
        }
    }

    // ************************************************
    // ORDENAMIENTO
    // ************************************************

    handleSort = (columna) => {
        const { sortConfig, datos } = this.state;

        let direction = "asc";
        if (sortConfig && sortConfig.campo === columna.campo && sortConfig.direction === "asc") {
            direction = "desc";
        }

        const comparador = this.comparadorPorTipo(columna.tipo, direction);

        const sortedDatos = [...datos].sort((a, b) => comparador(a, b, columna.campo));

        this.setState({
            datos: sortedDatos,
            sortConfig: { campo: columna.campo, direction }
        });
    }

    comparadorPorTipo = (tipo, direction) => {
        return (a, b, campo) => {
            let valorA = getValorCampo(a, campo, "");
            let valorB = getValorCampo(b, campo, "");

            if (valorA === null || valorA === undefined) valorA = "";
            if (valorB === null || valorB === undefined) valorB = "";

            switch (tipo) {
                case CELLTIPO_FECHA: {
                    const fechaA = new Date(valorA).getTime() || 0;
                    const fechaB = new Date(valorB).getTime() || 0;
                    return direction === "asc" ? fechaA - fechaB : fechaB - fechaA;
                }
                case CELLTIPO_CADENA:
                case CELLTIPO_MEMO: {
                    const strA = String(valorA).toLowerCase();
                    const strB = String(valorB).toLowerCase();
                    if (strA < strB) return direction === "asc" ? -1 : 1;
                    if (strA > strB) return direction === "asc" ? 1 : -1;
                    return 0;
                }
                case CELLTIPO_ESTADOROW:
                case CELLTIPO_ELIMINARROW:
                case CELLTIPO_ICONOACCION:
                    return 0;
                default: {
                    const numA = parseFloat(valorA) || 0;
                    const numB = parseFloat(valorB) || 0;
                    return direction === "asc" ? numA - numB : numB - numA;
                }
            }
        };
    };

    // ************************************************
    // ACTUALIZACION DE DATOS
    // ************************************************

    /* USO:
    this.tablaRef.current.updateRow(123, {
        estado: "ACTIVO",
        fecha: "2026-01-05"
        });
     */
    updateRow = (codigo, nuevosDatos) => {
        this.setState(prevState => {
            const nuevosDatosTabla = prevState.datos.map(row =>
                row.codigo === codigo
                    ? { ...row, ...nuevosDatos }
                    : row
            );

            const nuevasFilas = new Set(prevState.filasModificadas);
            nuevasFilas.add(codigo);

            return {
                datos: nuevosDatosTabla,
                filasModificadas: nuevasFilas
            };
        });

        Object.keys(nuevosDatos).forEach(campo => {
            this.enqueueFlash(codigo, campo);
        });

    };

    /* USO:
        this.tablaRef.current.updateCell(123, "estado", "CERRADO");
     */
    updateCell = (codigo, campo, valor) => {

        this.setState(prevState => {
            const nuevosDatos = prevState.datos.map(row =>
                row.codigo === codigo
                    ? { ...row, [campo]: valor }
                    : row
            );

            const nuevasFilas = new Set(prevState.filasModificadas);
            nuevasFilas.add(codigo);

            return {
                datos: nuevosDatos,
                filasModificadas: nuevasFilas
            };
        });

        // Flash agrupado
        this.enqueueFlash(codigo, campo);
    };


    replaceAllRows = (nuevosDatos) => {
        this.setState({ datos: nuevosDatos });
    };

    clearModifiedFlags = () => {
        this.setState({
            filasModificadas: new Set(),
            celdasActualizadas: {}
        });
    };

    // ************************************************
    // COLA DE MODIFICACIONES FLASH
    // ************************************************

    enqueueFlash = (codigo, campo) => {
        const cellKey = `${codigo}|${campo}`;
        const now = new Date();

        this.flashBuffer.add(cellKey);

        // Guardamos timestamp (persistente)
        this.setState(prev => ({
            celdasTimestamp: {
                ...prev.celdasTimestamp,
                [cellKey]: now
            }
        }));

        if (this.flashFlushTimer)
            return;

        this.flashFlushTimer = setTimeout(() => {
            this.flushFlashBuffer();
        }, FLASH_BATCH_WINDOW);
    };

    //Para centralizar la aplicacion de nuevos cambios de una sola vez.
    //Así solo se hace un setState y no uno por cada cambio.
    flushFlashBuffer = () => {
        const nuevasCeldas = {};

        this.flashBuffer.forEach(key => {
            nuevasCeldas[key] = true;
        });

        this.flashBuffer.clear();
        this.flashFlushTimer = null;

        this.setState(prev => ({
            celdasActualizadas: {
                ...prev.celdasActualizadas,
                ...nuevasCeldas
            }
        }));

        // Programar limpieza global (un solo timeout)
        if (this.flashClearTimer) {
            clearTimeout(this.flashClearTimer);
        }

        this.flashClearTimer = setTimeout(() => {
            this.setState({ celdasActualizadas: {} });
        }, FLASH_DURATION);


    };

    clearModificationInfo = () => {
        this.setState({
            filasModificadas: new Set(),
            celdasTimestamp: {}
        });
    };

    // ************************************************
    // RENDER HELPERS
    // ************************************************

    renderEstado = (numFila, columna, elemento) => {
        return (
            <div
                style={{ cursor: "default" }}
                onClick={() => this.setState({ elementoSeleccionadoCodigo: elemento.codigo })}
            >
                {numFila+1}
            </div>
        );
    }

    renderEliminar = (numFila, columna, elemento) => {
        const { onEliminar } = this.props;
        return (
            <div
                style={{ cursor: "pointer", fontSize: "18px", textAlign: "center" }}
                onClick={() => { onEliminar && onEliminar(elemento); }}
            >
                <Icon icon={"trash"} />
            </div>
        );
    }

    renderCadena = (numFila, columna, elemento) => {
        let valor = getValorCampo(elemento, columna.campo, "");
        return (<div>{valor}</div>);
    }

    renderMemo = (numFila, columna, elemento) => {
        let valor = elemento[columna.campo];
        return (<div>{valor}</div>);
    }
    renderHTML = (numFila, columna, elemento) => {
        let valor = elemento[columna.campo];
        const limpio = DOMPurify.sanitize(valor || "");
        return <div dangerouslySetInnerHTML={{ __html: limpio }} />;
    }

    renderFecha = (numFila, columna, elemento) => {
        let valor = elemento[columna.campo];
        return (
            <div>
                {valor ? new Date(valor).toLocaleDateString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                }) : ""}
            </div>
        );
    }

    renderIconoAccion = (numFila, columna, elemento) => {
        let icono = columna.icon;
        let onAccion = columna.onAccion;
        return (
            <div
                style={{ cursor: "pointer", fontSize: "18px", textAlign: "center" }}
                onClick={() => { onAccion && onAccion(elemento); }}
            >
                <Icon icon={icono} />
            </div>
        );
    }

    formatHora = (date) => {
        if (!date) return "";
        return date.toLocaleTimeString("es-ES", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit"
        });
    };




    // ************************************************
    // RENDER (encabezado, filas y celdas) con sticky left
    // ************************************************

    renderEncabezado = ()  => {
        const { columnas, colWidths } = this.state;
        const { encabezadoFontSize } = this.props;
        let encabezadoFontSizeReal = (encabezadoFontSize ? encabezadoFontSize : 12) + "px";

        // importante: resetear left acumulado antes de empezar
        let acumuladoLeft = 0;

        return (
            <thead>
            <tr>
                {columnas.map((columna, index) => {
                    let estiloSticky = {};
                    if (columna.fija) {
                        estiloSticky = {
                            position: "sticky",
                            left: acumuladoLeft,
                            zIndex: 10, // más alto que las celdas de datos
                            backgroundColor: "#eee",
                        };
                        acumuladoLeft += this.state.colWidths[index];
                    }

                    return (
                        <th
                            key={columna.campo + "_head"}
                            style={{
                                ...estiloSticky,
                                border: "1px solid #ccc",
                                width: colWidths[index],
                                padding: "8px",
                                fontSize: encabezadoFontSizeReal,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                position: "sticky",
                                top: 0, // se mantiene fijo al hacer scroll vertical
                                zIndex: columna.fija ? 11 : 9, // para que se superponga correctamente
                                backgroundColor: "#eee",
                                userSelect: "none",
                                cursor: "default",
                            }}
                            onMouseMove={(e) => this.handleMouseMoveTh(e, index)}
                            onMouseLeave={() => this.setState({ hoverCol: null })}
                            onMouseDown={(e) => this.handleMouseDownTh(e, index)}
                            onDoubleClick={(e) => this.autoResizeCol(e, index)}
                            onClick={() => this.handleSort(columna)}
                        >
                            {columna.titulo}
                            {this.state.sortConfig?.campo === columna.campo && (
                                <span style={{ marginLeft: "4px" }}>
                                  {this.state.sortConfig.direction === "asc" ? "▲" : "▼"}
                                </span>
                            )}
                        </th>
                    );
                })}
            </tr>
            </thead>
        );
    };


    renderColumnaDato = (numFila, columna, elemento, indexColumna, estiloSticky = {}) => {
        const {campoClave, fontSize} = this.props;
        const {colWidths, celdasActualizadas, celdasTimestamp, filasModificadas} = this.state;

        let contenido;
        switch (columna.tipo) {
            case CELLTIPO_ESTADOROW:
                contenido = this.renderEstado(numFila, columna, elemento);
                break;
            case CELLTIPO_ELIMINARROW:
                contenido = this.renderEliminar(numFila, columna, elemento);
                break;
            case CELLTIPO_CADENA:
                contenido = this.renderCadena(numFila, columna, elemento);
                break;
            case CELLTIPO_FECHA:
                contenido = this.renderFecha(numFila, columna, elemento);
                break;
            case CELLTIPO_MEMO:
                contenido = this.renderMemo(numFila, columna, elemento);
                break;
            case CELLTIPO_HTML:
                contenido = this.renderHTML(numFila, columna, elemento);
                break;
            case CELLTIPO_ICONOACCION:
                contenido = this.renderIconoAccion(numFila, columna, elemento);
                break;
            default:
                contenido = null;
        }

        let fontSizeReal= (fontSize ? fontSize: 12) + "px";
        let estiloBase = {
            border: "1px solid #ccc",
            padding: "8px",
            verticalAlign: "top",
            width: colWidths[indexColumna],
            fontSize: fontSizeReal,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",

            userSelect: "none",      // evita selección de texto
            caretColor: "transparent" // oculta cursor parpadeante
        };

        //Vamos si la celda se ha actualizado para que parpadee
        let cellKey = `${elemento.codigo}|${columna.campo}`;
        let celdaActualizada = celdasActualizadas[cellKey];
        let timestamp = celdasTimestamp[cellKey];
        if (celdaActualizada) {
            estiloBase.backgroundColor = "#d4edda"; // verde suave
            estiloBase.transition = "background-color 0.6s ease";
        }

        if (columna.tipo === CELLTIPO_MEMO || columna.tipo === CELLTIPO_HTML ) {
            estiloBase.whiteSpace = "pre-wrap";
            estiloBase.wordBreak = "break-word";
            estiloBase.maxWidth = "300px";
        }
        if (columna.tipo === CELLTIPO_ICONOACCION) {
            estiloBase.textAlign = "center";
        }
        /*
        if (columna.tipo === CELLTIPO_ESTADOROW) {
            estiloBase.textAlign = "center";
            estiloBase.backgroundColor = "lightgrey";
        }
        */
        if (columna.tipo === CELLTIPO_ESTADOROW) {
            estiloBase.textAlign = "center";
            estiloBase.backgroundColor = "#eee";
            estiloBase.fontWeight = "bold";
            estiloBase.cursor = "default";
        }


        let estiloFinal = { ...estiloBase, ...estiloSticky };

        let campoClaveReal= campoClave ? campoClave : "codigo";
        let claveElemento= columna.campo + "_" + elemento[campoClaveReal];

        //Tooltip de modificacion
        const filaModificada = filasModificadas.has(elemento.codigo);
        let tooltip = null;
        if (timestamp && filaModificada) {
            tooltip = `Modificado por WS a las ${this.formatHora(timestamp)}`;
        }

        return (
            <td key={claveElemento} style={estiloFinal} title={tooltip}>
                {contenido}
            </td>
        );
    };

    renderDatos= ()  => {
        const {columnas, datos, elementoSeleccionadoCodigo, filasModificadas} = this.state;
        const {filasZebra, filasZebraColores, estilizadorRow} = this.props;

        const filasZebraReal = (filasZebra === undefined ? true : filasZebra);
        const filasZebraColoresReal = filasZebraColores && filasZebraColores.length === 2 ? filasZebraColores : filasZebraColoresDefecto;

        return datos.map ((elemento, indexRow) => {
            let estiloRow = { verticalAlign: "top" };
            let filaSeleccionada = elemento.codigo === elementoSeleccionadoCodigo;
            let filaModificada = filasModificadas.has(elemento.codigo);

            //Aplicamos los estilos en orden inverso de prioridad

            //1.Zebra
            if (filasZebraReal)
                estiloRow.backgroundColor = (indexRow % 2 === 0) ? filasZebraColoresReal[0]: filasZebraColoresReal[1];

            // 2. Selección (sobrescribe zebra)
            if (filaSeleccionada) {
                estiloRow.backgroundColor = "#cce5ff";
                estiloRow.outline = "2px solid #66afe9";
                estiloRow.outlineOffset = "-2px";
            }

            //3. Modificacion
            if (filaModificada) {
                estiloRow.boxShadow = "inset 4px 0 0 #ffc107";
            }

            // 4. Estilizador externo (máxima prioridad)
            if (typeof estilizadorRow === "function") {
                const estiloExtra = estilizadorRow(elemento, indexRow);
                if (estiloExtra) {
                    estiloRow = { ...estiloRow, ...estiloExtra };
                }
            }

            let acumuladoLeft = 0;

            return (
                <tr key={elemento.codigo} style={estiloRow}>
                    {
                        columnas.map(( columna, indexColumna) => {
                            let estiloSticky = {};
                            if (columna.fija) {
                                estiloSticky = {
                                    position: "sticky",
                                    left: acumuladoLeft,
                                    zIndex: 5,
                                    backgroundColor: estiloRow.backgroundColor || "#fff"
                                };

                                if (columna.tipo === CELLTIPO_ESTADOROW) {
                                    estiloSticky.zIndex = 8;
                                    estiloSticky.backgroundColor = "#eee";
                                }

                                acumuladoLeft += this.state.colWidths[indexColumna];
                            }
                            return this.renderColumnaDato(indexRow, columna, elemento, indexColumna, estiloSticky);
                        })
                    }
                </tr>
            )
        });
    }

    render() {
        const {ancho, alto} = this.props;
        let estiloEnvolvente= {
            overflow: "auto",
            border: "1px solid #ccc",
            padding: "2px"
        }
        if (ancho) estiloEnvolvente.width= ancho;
        if (alto) estiloEnvolvente.height= alto;

        return (
            <div style={estiloEnvolvente} >
                <table style={{borderCollapse: "collapse", width: "100%", tableLayout: "fixed" }}>
                    {this.renderEncabezado()}
                    <tbody>
                    {this.renderDatos()}
                    </tbody>
                </table>
            </div>
        );
    }
}

Tabla.propTypes = {
    datos: PropTypes.array,
    columnas: PropTypes.array,
    campoClave: PropTypes.string,
    encabezadoFontSize: PropTypes.number,
    fontSize: PropTypes.number,
    ancho: PropTypes.number,
    alto: PropTypes.number,
    estilizadorRow: PropTypes.func,
    filasZebra: PropTypes.bool,
    filasZebraColores: PropTypes.arrayOf(PropTypes.string),
    mostrarEliminar: PropTypes.bool,
    onEliminar: PropTypes.func,
}

export default Tabla